import { Box, Grid, MenuItem } from '@mui/material';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultSelect } from '../../../../components/utils/Select';
import {
  useGetTranslatedMetricGroupName,
  useMetricDetailsMap,
} from '../../../api/metrics/hooks';
import { CohortMetricId, MetricId } from '../../../api/types-graphql';
import { LanguageString } from '../../../common/components/dashboards/types';
import { HeaderFooterLayout } from '../../../common/components/misc/HeaderFooterLayout';
import { Skeleton } from '../../../common/components/Skeleton';
import {
  useFieldLabelFormatter,
  useStateWithLocalStorage,
} from '../../../common/hooks';
import { useGlobalLocaleContext } from '../../../context/contexts';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
} from '../../../types';
import {
  DEFAULT_SIMULATION_METRICS,
  DEFAULT_SIMULATION_TIME_SELECTION_TYPE,
  DEFAULT_SIMULATION_YEAR_RANGE,
  SimulationChart,
  SimulationMetricId,
  SimulationTimeSelectionType,
  SimulationYearRange,
} from './HcSimulationChart';

const NO_SEGMENTATION = 'NO_SEGMENTATION' as const;

export const DEFAULT_HC_SIMULATION_SEGMENT_FIELDS: DataFieldWithDataType[] = [
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.AGE_GROUP,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.FUNCTION,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.EMPLOYMENT_TYPE,
  },
  {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.JOB_GRADE,
  },
];

interface HcSimulationPageProps {
  segmentFields?: DataFieldWithDataType[];
  simulationMetrics?: readonly SimulationMetricId[];
  simulationYearRange?: SimulationYearRange;
  simulationTimeSelectionType?: SimulationTimeSelectionType;
  /** Optional localized page header. Supplied per domain by the dashboard config. */
  header?: LanguageString;
  /** Optional localized page footer. Supplied per domain by the dashboard config. */
  footer?: LanguageString;
}

export const HcSimulationPage: React.FC<HcSimulationPageProps> = ({
  segmentFields = DEFAULT_HC_SIMULATION_SEGMENT_FIELDS,
  simulationMetrics = DEFAULT_SIMULATION_METRICS,
  simulationYearRange = DEFAULT_SIMULATION_YEAR_RANGE,
  simulationTimeSelectionType = DEFAULT_SIMULATION_TIME_SELECTION_TYPE,
  header,
  footer,
}) => {
  const { t } = useTranslation();
  const locale = useGlobalLocaleContext();
  const resolvedHeader = header?.[locale.selected];
  const resolvedFooter = footer?.[locale.selected];
  const [selectedMetric, setSelectedMetric] =
    useStateWithLocalStorage<SimulationMetricId>(
      CohortMetricId.Pm0417_2SimulatedHeadcountCount,
      'simulation-page-selectedMetric'
    );
  const effectiveSelectedMetric = simulationMetrics.includes(selectedMetric)
    ? selectedMetric
    : simulationMetrics[0];
  const [segmentDimension, setSegmentDimension] =
    useStateWithLocalStorage<DataFieldWithDataType | null>(
      null,
      'hc-simulation-page-segmentDimension'
    );
  const formatFieldLabel = useFieldLabelFormatter();
  const metricDetailsMap = useMetricDetailsMap();
  const getMetricGroupName = useGetTranslatedMetricGroupName();
  const availableSimulationMetrics = simulationMetrics.filter(
    (metric) => metricDetailsMap[metric as unknown as MetricId] != null
  );
  const effectiveAvailableMetric = availableSimulationMetrics.includes(
    effectiveSelectedMetric
  )
    ? effectiveSelectedMetric
    : availableSimulationMetrics[0];

  if (availableSimulationMetrics.length === 0 || !effectiveAvailableMetric) {
    return (
      <Box sx={{ height: '100%', padding: 2 }}>
        {t('common:customDashboard.noData')}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HeaderFooterLayout title={resolvedHeader} footer={resolvedFooter}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Grid container>
              <Grid item xs={2}>
                <DefaultSelect
                  value={effectiveAvailableMetric}
                  onChange={(evt) => {
                    const value = evt.target.value as SimulationMetricId;
                    setSelectedMetric(value);
                  }}
                >
                  {availableSimulationMetrics.map((metric) => {
                    const metricDetails =
                      metricDetailsMap[metric as unknown as MetricId];
                    return (
                      <MenuItem key={metric} value={metric}>
                        {getMetricGroupName(metricDetails.metricGroupId)}
                      </MenuItem>
                    );
                  })}
                </DefaultSelect>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={2}>
                <DefaultSelect
                  value={segmentDimension?.dataField ?? NO_SEGMENTATION}
                  onChange={(evt) => {
                    const value = evt.target.value;
                    if (value === NO_SEGMENTATION) {
                      setSegmentDimension(null);
                    } else {
                      const field = segmentFields.find(
                        (f) => f.dataField === value
                      );
                      if (field) {
                        setSegmentDimension(field);
                      }
                    }
                  }}
                >
                  {segmentFields.map((field) => (
                    <MenuItem key={field.dataField} value={field.dataField}>
                      {formatFieldLabel(field)}
                    </MenuItem>
                  ))}
                  <MenuItem value={NO_SEGMENTATION}>
                    {t('common:commonValues.misc.total')}
                  </MenuItem>
                </DefaultSelect>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Suspense fallback={<Skeleton />}>
              <SimulationChart
                key={segmentDimension?.dataField ?? 'total'}
                segmentByDataField={segmentDimension}
                metricId={effectiveAvailableMetric}
                simulationYearRange={simulationYearRange}
                simulationTimeSelectionType={simulationTimeSelectionType}
              />
            </Suspense>
          </Box>
        </Box>
      </HeaderFooterLayout>
    </Box>
  );
};
